/**
 * S12 — Agent Inventory (`/govern/agents`).
 *
 * Two-tab segmentation: "Governed" (ALGOS-managed) and "Shadow AI"
 * (detected unsanctioned usage). Each tab carries the same toolbar +
 * table contract, only the dataset differs.
 *
 * KPI strip · Search · Filters (department, vendor, status, risk band,
 * autonomy, drift) · Sort · View toggle (table / grid card).
 *
 * Row click → /govern/agents/:id (S13 — Agent Detail).
 */
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ShieldCheck,
  Eye,
  AlertTriangle,
  Activity,
  Filter,
  LayoutGrid,
  List,
  ArrowUpRight,
  Plus,
  RefreshCw,
  Sparkles,
  Radar,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import CsvExportButton from "@/components/data/CsvExportButton";
import { csvFilename } from "@/lib/csvExport";
import { cn } from "@/lib/utils";
import {
  RiskScorePill,
  DriftBadge,
  StatusPill,
  fmtAud,
  fmtPer1k,
} from "./components/AgentPrimitives";
import { optimisationNudge } from "@/lib/costRollups";
import { useAgentInventory } from "./useAgentInventory";

const SORTS = [
  { id: "risk-desc", label: "Risk score · high → low", cmp: (a, b) => b.riskScore - a.riskScore },
  { id: "risk-asc", label: "Risk score · low → high", cmp: (a, b) => a.riskScore - b.riskScore },
  { id: "burn-desc", label: "Monthly burn · high → low", cmp: (a, b) => b.monthlyCostAud - a.monthlyCostAud },
  { id: "value-desc", label: "Monthly value · high → low", cmp: (a, b) => b.monthlyValueAud - a.monthlyValueAud },
  { id: "cost-desc", label: "Monthly cost · high → low", cmp: (a, b) => b.monthlyCostAud - a.monthlyCostAud },
  { id: "name-asc", label: "Name · A → Z", cmp: (a, b) => a.name.localeCompare(b.name) },
  { id: "deployed-desc", label: "Deployed · newest", cmp: (a, b) => (b.deployedAt || "").localeCompare(a.deployedAt || "") },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "live", label: "Live" },
  { value: "monitored", label: "Monitored" },
  { value: "retired", label: "Retired" },
];

const RISK_OPTIONS = [
  { value: "", label: "All risk" },
  { value: "low", label: "Low" },
  { value: "med", label: "Medium" },
  { value: "high", label: "High" },
];

const AUTONOMY_OPTIONS = [
  { value: "", label: "All autonomy" },
  { value: "Suggest", label: "Suggest" },
  { value: "Recommend", label: "Recommend" },
  { value: "Act", label: "Act" },
];

const DRIFT_OPTIONS = [
  { value: "", label: "Any drift" },
  { value: "stable", label: "Stable" },
  { value: "warning", label: "Warning" },
  { value: "alert", label: "Alert" },
];

const AGENT_CSV_COLUMNS = [
  { header: "Agent ID",       accessor: (a) => a.id },
  { header: "Name",           accessor: (a) => a.name },
  { header: "Vendor",         accessor: (a) => a.vendorName || "" },
  { header: "Model",          accessor: (a) => a.model || "" },
  { header: "Department",     accessor: (a) => a.department || "" },
  { header: "Business unit",  accessor: (a) => a.businessUnit || "" },
  { header: "Owner",          accessor: (a) => a.ownerName || "" },
  { header: "Autonomy",       accessor: (a) => a.autonomy || "" },
  { header: "Risk score",     accessor: (a) => a.riskScore },
  { header: "Risk band",      accessor: (a) => a.riskBand || "" },
  { header: "Drift signal",   accessor: (a) => a.driftSignal || "" },
  { header: "Status",         accessor: (a) => a.status || "" },
  { header: "Monthly cost (AUD)",  accessor: (a) => a.monthlyCostAud  || 0 },
  { header: "Monthly value (AUD)", accessor: (a) => a.monthlyValueAud || 0 },
  { header: "$/1k token",     accessor: (a) => a.per1kToken || 0 },
  { header: "Deployed at",    accessor: (a) => a.deployedAt || "" },
];

export default function AgentInventory() {
  const { user } = useAuth();
  const { isCustomer, tenant } = useTenantData();
  // Phase 3b — read from the per-entity REST endpoint directly instead
  // of the monolith bundle slice. Filtering still happens client-side
  // against the full tenant list (it's small).
  const { items: agents, loading, error, lastSyncedAt, refetch } = useEntityList("/api/agents");
  const navigate = useNavigate();

  const {
    tab, setTab,
    view, setView,
    filters, setFilter,
    resetFilters,
    sortId, setSortId,
    departments, vendors,
    governed, monitored, shadow, dataset, filtered,
  } = useAgentInventory(agents, SORTS);

  const openAgent = useCallback(
    (a) => navigate(`/govern/agents/${a.id}`),
    [navigate],
  );

  if (loading && agents.length === 0) {
    return <InventorySkeleton />;
  }
  if (error) {
    return <InventoryError message={error.message} />;
  }
  if (isCustomer && agents.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSubmit={() => navigate("/identify/opportunities")} />;
  }

  const isGoverned = tab === "governed";
  const isMonitored = tab === "monitored";
  const isShadow = tab === "shadow";
  // "Monitored" reuses the Governed table — same shape, just a filtered slice.
  const useGovernedTable = isGoverned || isMonitored;
  const panelTestId = isShadow
    ? "agent-shadow-panel"
    : isMonitored
      ? "agent-monitored-panel"
      : "agent-governed-panel";

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="agent-inventory">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header />
      <KpiStrip governed={governed} monitored={monitored} shadow={shadow} />

      <Tabs
        current={tab}
        setCurrent={setTab}
        governedCount={governed.length}
        monitoredCount={monitored.length}
        shadowCount={shadow.length}
      />

      <div className="mt-4 surface-card shadow-e1 overflow-hidden" data-testid={panelTestId}>
        <Toolbar
          search={filters.search}
          setSearch={setFilter("search")}
          departments={departments}
          department={filters.department}
          setDepartment={setFilter("department")}
          vendors={vendors}
          vendor={filters.vendor}
          setVendor={setFilter("vendor")}
          status={filters.status}
          setStatus={setFilter("status")}
          risk={filters.risk}
          setRisk={setFilter("risk")}
          autonomy={filters.autonomy}
          setAutonomy={setFilter("autonomy")}
          drift={filters.drift}
          setDrift={setFilter("drift")}
          sortId={sortId}
          setSortId={setSortId}
          view={view}
          setView={setView}
          matchCount={filtered.length}
          totalCount={dataset.length}
          onReset={resetFilters}
          showGovernedFilters={useGovernedTable}
          lastSyncedAt={lastSyncedAt}
          syncing={loading}
          onRefetch={refetch}
          csvExport={
            <CsvExportButton
              rows={filtered}
              columns={AGENT_CSV_COLUMNS}
              filename={csvFilename(`agents-${tab}`, tenant?.id)}
              testId="agent-csv-export"
            />
          }
        />
        {useGovernedTable ? (
          view === "table"
            ? <GovernedTable items={filtered} onSelect={openAgent} sortId={sortId} setSortId={setSortId} />
            : <AgentGrid items={filtered} onSelect={openAgent} />
        ) : (
          <ShadowTable items={filtered} onSelect={openAgent} />
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Header() {
  return (
    <div className="relative">
      <p className="eyebrow">Govern · Agent Inventory</p>
      <h1 className="text-display-md text-ink-primary mt-1">All AI agents</h1>
      <p className="text-ink-secondary mt-1 max-w-2xl">
        A single source of truth for every AI agent — governed and shadow —
        across every vendor. Open any agent to inspect its risk profile,
        applied policies, drift signals, lineage and version history.
      </p>
    </div>
  );
}

function KpiStrip({ governed, monitored, shadow }) {
  const liveCount = governed.filter((a) => a.status === "live").length;
  const highRisk = governed.filter((a) => a.riskBand === "high").length;
  const drifting = governed.filter((a) => a.driftSignal === "warning" || a.driftSignal === "alert").length;
  const monitoredHighRisk = monitored.filter((a) => a.riskBand === "high").length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
      <KpiTile
        label="Governed agents"
        value={governed.length.toString()}
        delta={`${liveCount} live`}
        deltaTone="up"
        hint="across all vendors"
        icon={ShieldCheck}
        tone="tralenex"
        testId="agent-kpi-governed"
      />
      <KpiTile
        label="Monitored agents"
        value={monitored.length.toString()}
        delta={monitoredHighRisk > 0 ? `${monitoredHighRisk} high-risk` : "heightened review"}
        deltaTone={monitoredHighRisk > 0 ? "down" : "neutral"}
        hint="under closer watch"
        icon={Radar}
        tone="low"
        testId="agent-kpi-monitored"
      />
      <KpiTile
        label="Shadow AI detected"
        value={shadow.length.toString()}
        delta="Bring under governance"
        deltaTone={shadow.length > 0 ? "down" : "up"}
        hint="unsanctioned usage"
        icon={Eye}
        tone="risk"
        testId="agent-kpi-shadow"
      />
      <KpiTile
        label="High-risk agents"
        value={highRisk.toString()}
        delta="Need closer review"
        deltaTone={highRisk > 0 ? "neutral" : "up"}
        hint="risk score ≥ 70"
        icon={AlertTriangle}
        tone="gold"
        testId="agent-kpi-high-risk"
      />
      <KpiTile
        label="Drift signals"
        value={drifting.toString()}
        delta="warning + alert"
        deltaTone={drifting > 0 ? "down" : "up"}
        hint="last 7 days"
        icon={Activity}
        tone="opp"
        testId="agent-kpi-drift"
      />
    </div>
  );
}

function Tabs({ current, setCurrent, governedCount, monitoredCount, shadowCount }) {
  return (
    <div className="mt-6 flex items-center gap-1 border-b border-border-subtle" data-testid="agent-tabs">
      <TabButton
        active={current === "governed"}
        onClick={() => setCurrent("governed")}
        label="Governed"
        count={governedCount}
        testId="agent-tab-governed"
        icon={ShieldCheck}
      />
      <TabButton
        active={current === "monitored"}
        onClick={() => setCurrent("monitored")}
        label="Monitored"
        count={monitoredCount}
        testId="agent-tab-monitored"
        icon={Radar}
      />
      <TabButton
        active={current === "shadow"}
        onClick={() => setCurrent("shadow")}
        label="Shadow AI"
        count={shadowCount}
        testId="agent-tab-shadow"
        icon={Eye}
        warn
      />
    </div>
  );
}

function TabButton({ active, onClick, label, count, testId, icon: Icon, warn }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-2 px-4 h-10 border-b-2 -mb-px transition-colors",
        active
          ? "border-tralenex-600 text-ink-primary"
          : "border-transparent text-ink-secondary hover:text-ink-primary",
      )}
    >
      {Icon && <Icon size={14} className={warn && !active ? "text-shadow-red" : undefined} />}
      <span className="text-sm font-semibold">{label}</span>
      <span
        className={cn(
          "inline-flex items-center justify-center min-w-[20px] h-[20px] rounded px-1.5 text-[10px] font-bold tabular-nums",
          active ? "bg-tralenex-600 text-white" : warn ? "bg-shadow-red/10 text-shadow-red" : "bg-inset text-ink-secondary",
        )}
      >
        {count}
      </span>
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Toolbar(props) {
  const {
    search, setSearch,
    departments, department, setDepartment,
    vendors, vendor, setVendor,
    status, setStatus,
    risk, setRisk,
    autonomy, setAutonomy,
    drift, setDrift,
    sortId, setSortId,
    view, setView,
    matchCount, totalCount,
    onReset,
    showGovernedFilters,
    lastSyncedAt, syncing, onRefetch,
    csvExport,
  } = props;

  return (
    <div className="px-4 pt-3 pb-3 border-b border-border-subtle space-y-3" data-testid="agent-toolbar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents, owners, vendors, purpose…"
            className="w-full pl-8 pr-3 h-8 text-sm rounded-md border border-border-subtle bg-white focus:outline-none focus:border-tralenex-400"
            data-testid="agent-search-input"
          />
        </div>
        <Select value={department} onChange={setDepartment} testId="agent-filter-department">
          <option value="">All departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </Select>
        <Select value={vendor} onChange={setVendor} testId="agent-filter-vendor">
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
        </Select>
        {showGovernedFilters && (
          <>
            <Select value={status} onChange={setStatus} testId="agent-filter-status">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Select value={risk} onChange={setRisk} testId="agent-filter-risk">
              {RISK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Select value={autonomy} onChange={setAutonomy} testId="agent-filter-autonomy">
              {AUTONOMY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Select value={drift} onChange={setDrift} testId="agent-filter-drift">
              {DRIFT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </>
        )}
        <Select value={sortId} onChange={setSortId} testId="agent-sort-select">
          {SORTS.map((s) => <option key={s.id} value={s.id}>{`Sort · ${s.label}`}</option>)}
        </Select>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-meta text-ink-secondary hover:bg-inset"
          data-testid="agent-reset-filters"
        >
          <RefreshCw size={11} /> Reset
        </button>
        <span className="flex-1" />
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={syncing}
          onRefetch={onRefetch}
          testId="agent-inventory-synced"
        />
        <span className="text-meta text-ink-secondary tabular-nums" data-testid="agent-match-count">
          {matchCount} / {totalCount}
        </span>
        {csvExport}
        <div className="inline-flex rounded-md border border-border-subtle overflow-hidden" data-testid="agent-view-toggle">
          <ViewToggleBtn active={view === "table"} onClick={() => setView("table")} icon={List} label="Table" testId="agent-view-table" />
          <ViewToggleBtn active={view === "grid"} onClick={() => setView("grid")} icon={LayoutGrid} label="Grid" testId="agent-view-grid" />
        </div>
      </div>
    </div>
  );
}

function Select({ value, onChange, testId, children }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-8 px-2 text-sm rounded-md border border-border-subtle bg-white focus:outline-none focus:border-tralenex-400"
    >
      {children}
    </select>
  );
}

function ViewToggleBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1 h-8 px-2.5 text-meta font-semibold transition-colors",
        active ? "bg-tralenex-600 text-white" : "bg-white text-ink-secondary hover:bg-inset",
      )}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function GovernedTable({ items, onSelect, sortId, setSortId }) {
  const burnSortActive = sortId === "burn-desc";
  const toggleBurn = () => setSortId(burnSortActive ? "risk-desc" : "burn-desc");

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-inset/40 border-b border-border-subtle">
          <tr>
            {["Agent", "Vendor · Model", "Department", "Owner", "Autonomy", "Risk", "Drift", "Status"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">
                {h}
              </th>
            ))}
            <th className="px-3 py-2 text-right text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">
              <button
                type="button"
                onClick={toggleBurn}
                data-testid="agent-col-burn-sort"
                aria-pressed={burnSortActive}
                className={cn(
                  "inline-flex items-center gap-1 hover:text-ink-primary transition-colors",
                  burnSortActive && "text-ink-primary",
                )}
                title="Sort by monthly burn"
              >
                Burn / mo
                <span aria-hidden="true" className={cn("text-[10px]", burnSortActive ? "opacity-100" : "opacity-30")}>↓</span>
              </button>
            </th>
            <th className="px-3 py-2 text-right text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">$/1k tok</th>
            <th className="px-3 py-2 text-right text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">Value / mo</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {items.map((a) => {
            const nudge = optimisationNudge(a);
            return (
            <tr
              key={a.id}
              onClick={() => onSelect(a)}
              className="hover:bg-inset/40 cursor-pointer transition-colors"
              data-testid={`agent-row-${a.id}`}
            >
              <td className="px-3 py-2.5">
                <p className="font-medium text-ink-primary truncate max-w-[260px]">{a.name}</p>
                <p className="text-meta text-ink-secondary font-mono tabular-nums">{a.id}</p>
                {nudge && (
                  <span
                    className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-[0.04em] bg-gold-300/15 text-gold-700"
                    data-testid={`agent-nudge-${a.id}`}
                    title={`Quality impact: ${nudge.qualityImpact}`}
                  >
                    <Sparkles size={9} />
                    Switch model → save ~{fmtAud(nudge.savePerMo)}/mo
                  </span>
                )}
              </td>
              <td className="px-3 py-2.5">
                <p className="text-ink-primary truncate max-w-[180px]">{a.vendorName}</p>
                <p className="text-meta text-ink-disabled font-mono truncate max-w-[180px]">{a.model}</p>
              </td>
              <td className="px-3 py-2.5 text-ink-secondary">{a.department}</td>
              <td className="px-3 py-2.5 text-ink-secondary">{a.ownerName}</td>
              <td className="px-3 py-2.5">
                <span className="text-meta font-semibold uppercase tracking-[0.06em] text-ink-secondary">
                  {a.autonomy}
                </span>
              </td>
              <td className="px-3 py-2.5">
                <RiskScorePill score={a.riskScore} band={a.riskBand} />
              </td>
              <td className="px-3 py-2.5">
                <DriftBadge signal={a.driftSignal} />
              </td>
              <td className="px-3 py-2.5">
                <StatusPill status={a.status} />
              </td>
              <td
                className={cn(
                  "px-3 py-2.5 text-right text-sm tabular-nums",
                  burnSortActive ? "text-ink-primary font-semibold" : "text-ink-primary",
                )}
                data-testid={`agent-burn-${a.id}`}
              >
                {fmtAud(a.monthlyCostAud)}
              </td>
              <td className="px-3 py-2.5 text-right text-meta text-ink-secondary font-mono tabular-nums" data-testid={`agent-per1k-${a.id}`}>
                {fmtPer1k(a.per1kToken)}
              </td>
              <td className="px-3 py-2.5 text-right text-meta text-ink-primary tabular-nums">{fmtAud(a.monthlyValueAud)}</td>
              <td className="px-3 py-2.5 text-ink-disabled"><ArrowUpRight size={14} /></td>
            </tr>
            );
          })}
          {items.length === 0 && (
            <tr>
              <td colSpan={12} className="px-6 py-10 text-center text-ink-secondary">
                No agents match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ShadowTable({ items, onSelect }) {
  return (
    <div className="overflow-x-auto">
      <div className="px-4 py-2.5 bg-shadow-red/5 border-b border-border-subtle flex items-center gap-2" data-testid="shadow-banner">
        <AlertTriangle size={13} className="text-shadow-red" />
        <p className="text-meta text-ink-primary">
          <span className="font-semibold text-shadow-red">{items.length} unsanctioned AI tool{items.length === 1 ? "" : "s"} detected</span> · Bring each under governance to apply policies and capture audit evidence.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-inset/40 border-b border-border-subtle">
          <tr>
            {["Tool", "Vendor", "Department", "Users", "First seen", "Risk", "Detected via", "Evidence", ""].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {items.map((a) => (
            <tr
              key={a.id}
              onClick={() => onSelect(a)}
              className="hover:bg-inset/40 cursor-pointer transition-colors"
              data-testid={`agent-row-${a.id}`}
            >
              <td className="px-3 py-2.5">
                <p className="font-medium text-ink-primary">{a.name}</p>
                <p className="text-meta text-ink-secondary font-mono tabular-nums">{a.id}</p>
              </td>
              <td className="px-3 py-2.5 text-ink-secondary">{a.vendorName}</td>
              <td className="px-3 py-2.5 text-ink-secondary">{a.department}</td>
              <td className="px-3 py-2.5 text-ink-primary tabular-nums">{a.usersDetected}</td>
              <td className="px-3 py-2.5 text-meta text-ink-secondary">{a.firstSeen}</td>
              <td className="px-3 py-2.5">
                <RiskScorePill score={a.riskScore} band="shadow" />
              </td>
              <td className="px-3 py-2.5 text-meta text-ink-secondary truncate max-w-[220px]">
                {a.detectedVia}
              </td>
              <td className="px-3 py-2.5 text-meta text-ink-primary tabular-nums">{a.evidenceCount} events</td>
              <td className="px-3 py-2.5 text-ink-disabled"><ArrowUpRight size={14} /></td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={9} className="px-6 py-10 text-center text-ink-secondary">
                No shadow AI detected.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function AgentGrid({ items, onSelect }) {
  return (
    <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="agent-grid">
      {items.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => onSelect(a)}
          className="text-left surface-card hover:shadow-e2 transition-shadow p-3.5 border border-border-subtle"
          data-testid={`agent-card-${a.id}`}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0">
              <p className="text-meta font-mono tabular-nums text-ink-disabled">{a.id}</p>
              <p className="text-sm font-semibold text-ink-primary truncate">{a.name}</p>
            </div>
            <RiskScorePill score={a.riskScore} band={a.riskBand} />
          </div>
          <p className="text-meta text-ink-secondary line-clamp-2 mb-2.5">{a.purpose}</p>
          <div className="flex items-center justify-between gap-2 text-meta text-ink-secondary">
            <span className="truncate">{a.vendorName}</span>
            <StatusPill status={a.status} />
          </div>
          <div className="mt-2 pt-2 border-t border-border-subtle grid grid-cols-3 gap-2 text-meta">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.06em] text-ink-disabled">Burn</p>
              <p className="text-ink-primary tabular-nums font-semibold" data-testid={`agent-card-burn-${a.id}`}>{fmtAud(a.monthlyCostAud)}</p>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.06em] text-ink-disabled">$/1k tok</p>
              <p className="text-ink-secondary tabular-nums font-mono" data-testid={`agent-card-per1k-${a.id}`}>{fmtPer1k(a.per1kToken)}</p>
            </div>
            <div className="min-w-0 text-right">
              <p className="text-[10px] uppercase tracking-[0.06em] text-ink-disabled">Value</p>
              <p className="text-risk-low tabular-nums font-semibold">{fmtAud(a.monthlyValueAud)}</p>
            </div>
          </div>
        </button>
      ))}
      {items.length === 0 && (
        <p className="col-span-full py-10 text-center text-ink-secondary">No agents match the current filters.</p>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function CustomerEmpty({ userName, onSubmit }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up" data-testid="agent-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-3xl">
        <p className="eyebrow">Govern · Agent Inventory</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          No agents yet — submit your first AI opportunity and ALGOS
          will create a governed agent record the moment it reaches stage 7
          of the deployment workflow. Shadow AI signals will appear here
          automatically once the integrations are connected.
        </p>
        <button
          type="button"
          onClick={onSubmit}
          className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="agent-empty-submit"
        >
          <Plus size={14} /> Submit first opportunity
        </button>
        <p className="mt-3 text-meta text-ink-disabled inline-flex items-center gap-1.5">
          <Filter size={11} /> Or connect your AI vendors in Vendors · Integration Hub
        </p>
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Phase 3b — load + error states for REST-fetched inventory.
// ───────────────────────────────────────────────────────────────────────────
function InventorySkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="agent-inventory-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[420px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function InventoryError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="agent-inventory-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Govern · Agent Inventory</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load the agent inventory</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}
